import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import TurndownService from 'turndown';
import { marked } from "marked";
import "../ChatForm/SearchBar.css";
import { doc, getDoc, updateDoc, arrayUnion, setDoc, increment } from "firebase/firestore";
import { useParams, useLocation } from "react-router-dom";
import { db, functions, storage } from "../../../firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ref as firebaseRef, uploadString, getDownloadURL, deleteObject } from "firebase/storage";
import axios from "axios";
import JSON5 from "json5";
import CSV from "csvtojson"; // Import csvtojson for CSV conversion
import { saveTableDataToFirestore } from "../../services/firestoreUtils";
import AgentSelector from "../Agents/AgentSelector.jsx";
import DocSelector from "../Docs/DocSelector.jsx";
// import { useAuth } from "../../../contexts/AuthContext";
import { getOAuthToken } from "../GooglePicker/googleAuth.js";
import { processChatOutput } from "../../utils/chatProcessor";
import { prepareSlideOutput } from "../../utils/slidesProcessor";
import { prepareGDocsContent } from "../../utils/exportProcessor";
import { makeId } from "../../utils/id";
import { useDropzone } from "react-dropzone";
import { useMergeLink } from "@mergeapi/react-merge-link";
import { authorizeAtlassian, createJiraIssue, createConfluencePage } from "../../services/atlassian";
import { authorizeLinear, getLinearTeams, createLinearIssue } from "../../services/linear.js";

// Firebase function references
const callGeminiAPI = httpsCallable(functions, "callGeminiAPI");
const streamGeminiAPI = httpsCallable(functions, "streamGeminiAPI"); // Replaced with fetch for streaming
const initializeGeminiChat = httpsCallable(functions, "initializeGeminiChat");
const initializeOpenRouterChat = httpsCallable(functions, "initializeOpenRouterChat");
const callOpenRouterAPI = httpsCallable(functions, "callOpenRouterAPI");
const createMergeTicket = httpsCallable(functions, "createMergeTicket");
const createMergeLinkToken = httpsCallable(functions, "createMergeLinkToken");
const exchangePublicToken = httpsCallable(functions, "exchangePublicToken");
const getJiraProjectsCallable = httpsCallable(functions, "getJiraProjects");
const getJiraEpicsCallable = httpsCallable(functions, "getJiraEpics");
const getJiraSprintsCallable = httpsCallable(functions, "getJiraSprints");
const createJiraIssueCallable = httpsCallable(functions, "createJiraIssue");
const getConfluenceSpacesCallable = httpsCallable(functions, "getConfluenceSpaces");
const createConfluencePageCallable = httpsCallable(functions, "createConfluencePage");
const atlassianAuthCallable = httpsCallable(functions, "atlassianAuth");
const atlassianCallbackCallable = httpsCallable(functions, "atlassianCallback");
const getLinearTeamsCallable = httpsCallable(functions, "getLinearTeams");
const createLinearIssueCallable = httpsCallable(functions, "createLinearIssue");


const GeminiComponent = forwardRef(({
  onSendData,
  showAnalyzeButton = true,
  onCreateVisualization,
  authUser,
  setComponents,
  onSlideCreated,
  tableToolbarRefs,
  setIsExporting,
  // onRequestGoogleSignIn,
  // createGoogleDocApiCall,
}, ref) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [chat, setChat] = useState(null);
  const [isResponseMinimized, setIsResponseMinimized] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [latestResponse, setLatestResponse] = useState("");
  const { viewId, slideId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isAgentMode = queryParams.get("agentMode") === "true";
  // const [currentViewUID, setCurrentViewUID] = useState("");
  // const [lastComponentId, setLastComponentId] = useState(0);
  // const [components, setComponents] = useState([]);
  // const [csvUrl, setCsvUrl] = useState(null);
  const currentUser = authUser;
  // const [uploadedFiles, setUploadedFiles] = useState([]);
  // const [searchString, setSearchString] = useState("");
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isNewAgentOpen, setIsNewAgentOpen] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null); // New state for current doc type
  const [availableDocs, setAvailableDocs] = useState([]); // To store all docs from DocsSelector
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isNewDocOpen, setIsNewDocOpen] = useState(false);
  const [googleAuthToken, setGoogleAuthToken] = useState(null);
  // const [mergeLinkToken, setMergeLinkToken] = useState(null);
  // const [mergeAccountToken, setMergeAccountToken] = useState(null);
  const [isJiraModalOpen, setIsJiraModalOpen] = useState(false);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [selectedJiraProject, setSelectedJiraProject] = useState(null);
  const [jiraEpics, setJiraEpics] = useState([]);
  const [selectedJiraEpic, setSelectedJiraEpic] = useState(null);
  const [jiraSprints, setJiraSprints] = useState([]);
  const [selectedJiraSprint, setSelectedJiraSprint] = useState(null);
  const [isConfluenceModalOpen, setIsConfluenceModalOpen] = useState(false);
  const [confluenceSpaces, setConfluenceSpaces] = useState([]);
  const [selectedConfluenceSpace, setSelectedConfluenceSpace] = useState(null);
  const [jiraExportSlides, setJiraExportSlides] = useState([]);

  const [isLinearModalOpen, setIsLinearModalOpen] = useState(false);
  const [linearTeams, setLinearTeams] = useState([]);
  const [selectedLinearTeam, setSelectedLinearTeam] = useState(null);
  const [linearExportSlides, setLinearExportSlides] = useState([]);
  const [linearAuthStatus, setLinearAuthStatus] = useState('unknown'); // 'unknown', 'connected', 'disconnected'
  const [isLinearConnectModalOpen, setIsLinearConnectModalOpen] = useState(false);

  const [atlassianAuthStatus, setAtlassianAuthStatus] = useState('unknown'); // 'unknown', 'connected', 'disconnected'
  const [isAtlassianConnectModalOpen, setIsAtlassianConnectModalOpen] = useState(false);
  const [atlassianService, setAtlassianService] = useState(''); // 'Jira' or 'Confluence'

  const checkAtlassianConnection = useCallback(async () => {
    if (!authUser) {
      setAtlassianAuthStatus('disconnected');
      return;
    }
    setAtlassianAuthStatus('unknown');
    try {
      // We only need to check one endpoint to verify the token is valid.
      await getJiraProjectsCallable();
      setAtlassianAuthStatus('connected');
    } catch (error) {
      if (error.code === 'failed-precondition') {
        setAtlassianAuthStatus('disconnected');
      } else {
        // Other errors (e.g., network) also mean we are effectively disconnected.
        setAtlassianAuthStatus('disconnected');
      }
    }
  }, [authUser]);

  useEffect(() => {
    checkAtlassianConnection();
  }, [checkAtlassianConnection]);

  useEffect(() => {
    const check = async () => {
      if (atlassianAuthStatus === 'connected') return;
      await checkAtlassianConnection();
    };
    window.addEventListener('focus', check);
    return () => window.removeEventListener('focus', check);
  }, [atlassianAuthStatus, checkAtlassianConnection]);

  const checkLinearConnection = useCallback(async () => {
    if (!authUser) {
      setLinearAuthStatus('disconnected');
      return;
    }
    setLinearAuthStatus('unknown');
    try {
      await getLinearTeamsCallable();
      setLinearAuthStatus('connected');
    } catch (error) {
      if (error.code === 'failed-precondition') {
        setLinearAuthStatus('disconnected');
      } else {
        setLinearAuthStatus('disconnected');
      }
    }
  }, [authUser]);

  useEffect(() => {
    checkLinearConnection();
  }, [checkLinearConnection]);

  useEffect(() => {
    const check = async () => {
      if (linearAuthStatus === 'connected') return;
      await checkLinearConnection();
    };
    window.addEventListener('focus', check);
    return () => window.removeEventListener('focus', check);
  }, [linearAuthStatus, checkLinearConnection]);

  // const auth = authUser;
  // const [agentInst, setAgentInst] = useState(null); // New state for agent instructions
  // const [isCreatingDoc, setIsCreatingDoc] = useState(false); // State for GDocs creation
  const [currentAgent, setCurrentAgent] = useState(null); // New state for current agent
  const [availableAgents, setAvailableAgents] = useState([]); // To store all agents from AgentSelector

  // console.log("setComponents: ", setComponents);
  const messagesEndRef = useRef(null);
  const lastInitializedAgentIdRef = useRef(null);
  const chatContainerRef = useRef(null);
  const agentSelectorRef = useRef(null);
  const docSelectorRef = useRef(null);
  const agentButtonRef = useRef(null);
  const docButtonRef = useRef(null);
  const lastSentDataRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const [isAgentRunning, setIsAgentRunning] = useState(false);
  const isSequenceRunningRef = useRef(false);
  const chatRef = useRef(chat);

  useEffect(() => {
    chatRef.current = chat;
  }, [chat]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (isAgentMode) {
      scrollToBottom();
    }
  }, [messages, isLoading, isAgentMode]);

  // useEffect(() => {
  //   // console.log("GeminiComponent: currentUser", currentUser);

  //   const fetchCurrentViewUID = async () => {
  //     setCurrentViewUID("current-view-uid");
  //   };

  //   fetchCurrentViewUID();
  // }, []);

  const toggleResponseView = () => {
    setIsResponseMinimized(!isResponseMinimized);
  };

  const openResponseView = () => {
    setIsResponseMinimized(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (chatContainerRef.current && !chatContainerRef.current.contains(event.target)) {
        setIsResponseMinimized(true);
      }

      if (
        isAgentModalOpen &&
        agentSelectorRef.current &&
        !agentSelectorRef.current.contains(event.target) &&
        agentButtonRef.current &&
        !agentButtonRef.current.contains(event.target)
      ) {
        setIsAgentModalOpen(false);
        setIsNewAgentOpen(false);
      }

      if (
        isDocModalOpen &&
        docSelectorRef.current &&
        !docSelectorRef.current.contains(event.target) &&
        docButtonRef.current &&
        !docButtonRef.current.contains(event.target)
      ) {
        setIsDocModalOpen(false);
        setIsNewDocOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAgentModalOpen, isDocModalOpen]);

  // const viewAgents = [];

  useEffect(() => {
    const initializeChat = async () => {
      // console.log("Initializing default chat session...");

      // --- Set loading true ---
      setIsLoading(true);
      try {
        // Initialize with Firebase function - we don't need to create the actual chat here
        // The chat will be created when sending messages via Firebase function
        let result;
        // if (window.location.host === "localhost:5173") {
        //   result = { data: { success: true } };
        // } else {
          result = await initializeGeminiChat({
            agentInstructions: {
              name: "Default Agent",
              instructions: "Default system instructions",
            },
          });
        // }

        if (result.data.success) {
          // Set a flag to indicate chat is ready
          setChat({ ready: true, type: "firebase" });
          console.log("Firebase chat session initialized.");
        }
      } catch (error) {
        console.error("Error initializing Firebase chat:", error);
        setMessages((prev) => [...prev, { text: `Error initializing chat: ${error.message}`, sender: "System" }]);
      } finally {
        // --- Set loading false ---
        setIsLoading(false);
      }
    };
    initializeChat();
  }, []); // Removed genAI dependency since we're using Firebase functions


  //////////////////////////////
  // -- Switch Agents -- //
  //////////////////////////////

  // const handleAgentDataLoad = (data) => {
  //   console.log("GeminiComponent: handleAgentDataLoad called with", data);
  //   setAgentInst(data); // Store the data in the state
  // };

  // Stable function for saving selected agent ID to the 'views' collection
  const saveLastSelectedAgentIdToView = useCallback(
    async (agentId) => {
      if (!viewId || !currentUser?.uid) {
        console.error("Cannot save last selected agent ID: viewId or currentUser.uid is missing.");
        return;
      }
      try {
        const viewDocRef = doc(db, "views", viewId);
        await updateDoc(viewDocRef, {
          lastSelectedAgentId: agentId,
          lastSelectedAgentTimestamp: new Date(), // Optional: for tracking
          // editedAt: new Date(),
          // editedBy: currentUser.uid,
        });
        console.log(`Saved last selected agent ID (${agentId}) to 'views' collection for view ${viewId}.`);
      } catch (error) {
        console.error("Error saving last selected agent ID to Firebase:", error);
      }
    },
    [viewId, currentUser?.uid]
  );

  // Stable handler for when AgentSelector triggers an agent selection
  const handleAgentSelectionFromSelector = useCallback(
    (agentData) => {
      console.log("GeminiComponent: Agent selected via AgentSelector", agentData);
      setCurrentAgent(agentData); // Update main state

      // GeminiComponent saves the selection to the 'views' doc.
      // AgentSelector itself saves to 'users.{uid}.agentCurrent' upon click.
      if (agentData && agentData.id) {
        saveLastSelectedAgentIdToView(agentData.id);
      }

    },
    [saveLastSelectedAgentIdToView]

  ); // Depends on the memoized save function

  // Stable function to load initial agent based on users.{uid}.agentCurrent
  const loadInitialAgentFromUserDoc = useCallback(async () => {
    if (!currentUser?.uid || !availableAgents || availableAgents.length === 0) {
      console.log("Cannot load initial agent: missing user, or no available agents.");
      return;
    }
    // Load the last selected agent ID from the user's document
    const userDocRef = doc(db, "users", currentUser.uid);
    const docSnap = await getDoc(userDocRef);
    let agentToSet = availableAgents[0]; // Default to the first agent

    if (docSnap.exists() && docSnap.data().agentCurrent) {
      // Check for the 'agentCurrent' field
      const lastId = docSnap.data().agentCurrent; // Get the agent ID from 'agentCurrent'
      const foundAgent = availableAgents.find((agent) => agent.id === lastId);
      if (foundAgent) agentToSet = foundAgent; // If found, use the saved agent
    }
    setCurrentAgent(agentToSet);
    console.log("Initial agent set to:", agentToSet?.name || "first available");
  }, [currentUser?.uid, availableAgents]); // Depends on user and the list of agents

  // Effect to load the initial agent once availableAgents list is populated
  useEffect(() => {
    if (currentUser?.uid && availableAgents.length > 0) {
      loadInitialAgentFromUserDoc();
    }
  }, [currentUser?.uid, availableAgents, loadInitialAgentFromUserDoc]); // Correct dependencies

  // useEffect(() => {
  //   // console.log("currentAgent has changed:", currentAgent);
  //   // Place the code you want to run here
  //   // This code will run whenever currentAgent changes

  //   if (currentAgent) {
  //     // console.log("Current Agent Name:", currentAgent.name);
  //     // console.log("Current Agent Instructions:", currentAgent.inst);
  //     // chat.systemInstruction = currentAgent.inst;
  //     setMessages((prevMessages) => [...prevMessages, { text:"Updating your agent to: "+currentAgent.name , sender: "AI" }]);
  //     chat._history = [{
  //       role: "user",
  //       parts: [{text: "your name is now: " + currentAgent.name+ ", these are your new instructions are, follow them closely:"+currentAgent.inst,}]
  //     },{
  //       role: "model",
  //       parts: [{text: "I am updating my name and instructions to: " + currentAgent.name+currentAgent.inst}]
  //     }
  //     ];
  //     console.log("Updated history:", chat._history);

  //     // ... do something with currentAgent ...
  //   }
  // }, [currentAgent]); // Dependency array: This effect runs when currentAgent changes

  const markdownTableToJson = (markdown) => {
    console.log("Input Markdown:", markdown);

    const lines = markdown
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line);
    console.log("Lines:", lines);

    // Find the separator row (usually |---|---|) to locate the header
    const separatorIdx = lines.findIndex(line => line.includes('---') && line.includes('|'));

    if (separatorIdx < 1) {
      console.error("Invalid Markdown table format. Separator not found or no header row.");
      return [];
    }

    const headers = lines[separatorIdx - 1]
      .split("|")
      .map((header) => header.trim())
      .filter((header) => header);
    console.log("Headers:", headers);

    if (headers.length === 0) {
      console.error("Invalid Markdown table format. No headers found.");
      return [];
    }

    const jsonArray = [];

    for (let i = separatorIdx + 1; i < lines.length; i++) {
      const rowCells = lines[i]
        .split("|")
        .map((cell) => cell.trim())
        .filter((cell) => cell);
      console.log(`Row ${i - 1} Cells:`, rowCells);

      if (rowCells.length !== headers.length) {
        console.warn(`Row ${i - 1} has a different number of cells than headers. Skipping this row.`);
        continue;
      }

      const rowData = {};

      for (let j = 0; j < rowCells.length; j++) {
        rowData[headers[j]] = rowCells[j];
      }

      jsonArray.push(rowData);
    }

    console.log("Converted JSON Array:", jsonArray);
    return jsonArray;
  };

  const addComponentToExistingSlide = useCallback(
    async (componentId, targetSlideId) => {
      if (!viewId || !targetSlideId) return;
      try {
        const docRef = doc(db, "views", viewId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const slides = data.slides || [];
          const slideIndex = slides.findIndex((s) => s.id === targetSlideId);
          if (slideIndex > -1) {
            slides[slideIndex].components.push(componentId);
            await updateDoc(docRef, { slides });
            console.log(`Added component ${componentId} to existing slide ${targetSlideId}`);
            if (onSlideCreated) {
              console.log("Triggering onSlideCreated for existing slide:", targetSlideId);
              onSlideCreated(targetSlideId);
            }
          }
        }
      } catch (error) {
        console.error("Error adding to existing slide:", error);
      }
    },
    [viewId, onSlideCreated]
  );

  const createSlideWithComponents = useCallback(
    async (componentIds) => {
      if (!viewId) return;
      const newSlideId = makeId(10);
      const components = Array.isArray(componentIds) ? componentIds : [componentIds];
      const newSlide = {
        id: newSlideId,
        components: components,
      };

      try {
        const docRef = doc(db, "views", viewId);
        await updateDoc(docRef, {
          slides: arrayUnion(newSlide),
        });

        if (typeof onSlideCreated === "function") {
          console.log("Triggering onSlideCreated for new slide:", newSlideId);
          onSlideCreated(newSlideId);
        } else {
          console.warn("onSlideCreated prop is missing or not a function. ViewPage will not update.");
        }
      } catch (error) {
        console.error("Error creating new slide:", error);
      }
    },
    [viewId, onSlideCreated]
  );

  const saveTextComponentToFirestore = useCallback(
    async (content) => {
      if (!viewId) return null;
      const newId = makeId(10);
      const newComponent = {
        type: "text-editor",
        id: newId,
        cards: 2,
        content: content,
      };

      try {
        const docRef = doc(db, "views", viewId);
        await updateDoc(docRef, {
          contents: arrayUnion(newComponent),
        });
        console.log(`Created text component ${newId}`);
        return newId;
      } catch (error) {
        console.error("Error creating text component:", error);
      }
      return null;
    },
    [viewId]
  );

  const saveDiagramComponentToFirestore = useCallback(
    async (content) => {
      if (!viewId) return null;
      const newId = makeId(10);
      const newComponent = {
        type: "diagram",
        id: newId,
        cards: 4,
        content: content,
      };

      try {
        const docRef = doc(db, "views", viewId);
        await updateDoc(docRef, {
          contents: arrayUnion(newComponent),
        });
        console.log(`Created diagram component ${newId}`);
        return newId;
      } catch (error) {
        console.error("Error creating diagram component:", error);
      }
      return null;
    },
    [viewId]
  );

  const handleSendData = useCallback(
    async (content, options = {}) => {
      const { returnIdOnly = false } = options;
      const markdownContent = marked(content);
      console.log("Sending data to Text Editor:", markdownContent);
      // onSendData({ text: markdownContent });

      const componentId = await saveTextComponentToFirestore(markdownContent);
      
      if (returnIdOnly) return componentId;

      if (componentId) {
        if (slideId) {
          await addComponentToExistingSlide(componentId, slideId);
        } else {
          await createSlideWithComponents([componentId]);
        }
        if (!isAgentMode) {setIsResponseMinimized(true);}
      }
    },
    [saveTextComponentToFirestore, createSlideWithComponents, addComponentToExistingSlide, slideId, isAgentMode]
  );

  const handleSendDataToFM = useCallback(async (content, options = {}) => {
    const { returnIdOnly = false } = options;
    if (!authUser || !authUser.uid) {
      console.error("No authenticated user found.");
      return;
    }

    const markdownTable = content;
    const jsonData = markdownTableToJson(markdownTable);
    console.log("Sending to Data Visualization. Final JSON Data:", JSON.stringify(jsonData, null, 2));

    if (jsonData.length > 0) {
      try {
        const newComponentID = await saveTableDataToFirestore(jsonData, viewId);
        
        if (returnIdOnly) {
          return newComponentID;
        }

        if (newComponentID) {
          if (slideId) {
            await addComponentToExistingSlide(newComponentID, slideId);
          } else {
            await createSlideWithComponents([newComponentID]);
          }
          setIsResponseMinimized(true);
        }
      } catch (error) {
        console.error("Error saving data to Firestore:", error);
      }
    } else {
      console.warn("No valid table data found in the message to send to Data Visualization.");
    }
  }, [authUser, viewId, createSlideWithComponents, addComponentToExistingSlide, slideId]);

  const handleSendToDiagram = useCallback(async (content, options = {}) => {
    const { returnIdOnly = false } = options;
    console.log("Sending data to Diagram:", content);

    const componentId = await saveDiagramComponentToFirestore(content);
    
    if (returnIdOnly) return componentId;

    if (componentId) {
      if (slideId) {
        await addComponentToExistingSlide(componentId, slideId);
      } else {
        await createSlideWithComponents([componentId]);
      }
      if (!isAgentMode) {setIsResponseMinimized(true);}
    }
  }, [saveDiagramComponentToFirestore, createSlideWithComponents, addComponentToExistingSlide, slideId, isAgentMode]);

  const handleBuildCanvas = useCallback(async (content) => {
    const blocks = processChatOutput(content);
    console.log("Processed Chat Blocks for Canvas:", blocks);

    const createdComponentIds = [];

    for (const block of blocks) {
      if (block.type === "table") {
        // Send table data to Firestore (creates Data Visualization)
        const id = await handleSendDataToFM(block.content, { returnIdOnly: true });
        if (id) createdComponentIds.push(id);
      } else if (block.type === "diagram") {
        // Send diagram data to Firestore (creates Diagram)
        const id = await handleSendToDiagram(block.content, { returnIdOnly: true });
        if (id) createdComponentIds.push(id);
      } else {
        // Send text data (creates Text Editor)
        const id = await handleSendData(block.content, { returnIdOnly: true });
        if (id) createdComponentIds.push(id);
      }
    }

    if (createdComponentIds.length > 0) {
      setIsResponseMinimized(true);
      await createSlideWithComponents(createdComponentIds);
    }
  }, [handleSendData, handleSendDataToFM, handleSendToDiagram, createSlideWithComponents]);

  const getFunctionUrl = (name) => {
    if (window.location.hostname === "localhost") {
      return `http://127.0.0.1:5001/${functions.app.options.projectId}/us-central1/${name}`;
    }
    // Assuming standard Firebase Functions URL format or rewrites
    // You might need to adjust this based on your specific hosting/region setup
    // If using rewrites: return `/${name}`;
    return `https://us-central1-${functions.app.options.projectId}.cloudfunctions.net/${name}`; // Standard Cloud Function URL
  };


  //// --- OPENROUTER TEST FUNCTION (IGNORE) --- \\\\
  const runOpenRouterTest = async () => {
    console.log("--- STARTING OPENROUTER TEST ---");

    // 1. Test Initialize
    try {
      console.log("Testing initializeOpenRouterChat...");
      const initResult = await initializeOpenRouterChat({
        agentInstructions: { name: "Test Agent", instructions: "System instructions test." },
      });
      console.log("initializeOpenRouterChat Result:", initResult.data);
    } catch (error) {
      console.error("initializeOpenRouterChat Failed:", error);
    }

    // 2. Test Call
    try {
      console.log("Testing callOpenRouterAPI...");
      const result = await callOpenRouterAPI({
          message: "Hello! This is a test message. Please reply with 'Test Successful'.",
          history: [],
          model: "google/gemini-2.0-flash-lite-preview-02-05:free",
      });

      console.log("callOpenRouterAPI Result:", result.data);
    } catch (error) {
      console.error("callOpenRouterAPI Failed:", error);
    }
    console.log("--- END OPENROUTER TEST ---");
  };

  const sendMessage = useCallback(
    async (messageInput, options = {}) => {
      const ensureOpen = options.ensureOpen !== false; // Default to true
      const activeAgent = options.agent || chatRef.current?.currentAgent; // Use passed agent or fallback to state
      const { file = null } = options;
      setIsSending(true);
      if (ensureOpen && !isAgentRunning) {
        setIsResponseMinimized(false);
      }

      const messageToSend = typeof messageInput === "string" ? messageInput : newMessage;
      if (!messageToSend.trim() && !file) {
        console.log("sendMessage blocked: message empty.");
        setIsSending(false); // Reset sending state if message is empty
        return;
      }

      console.log("sendMessage: Attempting to send:", messageToSend.substring(0, 200) + "..."); // Log start and beginning of message
      setIsLoading(true);
      setIsDrawerOpen(true);

      // Use existing chat or create a temporary one if not ready
      const currentChat = chatRef.current;
      const effectiveChat = currentChat?.ready ? currentChat : { ready: true, history: [], type: "firebase" };
      const maxRetries = 3;
      let attempt = 0;

      // Add User message immediately, before the retry loop
      setMessages((prevMessages) => [...prevMessages, { text: messageToSend, sender: "User" }, { text: "Thinking...", sender: "AI", id: "thinking-message" }]);

      while (attempt < maxRetries) {
        try {
          attempt++;
          console.log(`sendMessage: Attempt ${attempt} calling Firebase function...`);

          // Prepare the history to send to Firebase function
          const chatHistory = effectiveChat.history || [];

          // Add current agent context if available
          let historyWithAgent = chatHistory;
          if (activeAgent) {
            historyWithAgent = [
              {
                role: "user",
                parts: [
                  {
                    text: `## AGENT INSTRUCTIONS START ##\n\nYour name is ${activeAgent.name}. Your job role is: ${activeAgent.role}\n\nYour core instructions are:\n${activeAgent.inst}\n\nFollow these instructions strictly for all subsequent interactions in this chat session. Do not preface reponses, go straight to answers and be direct. Numbers in tables should always be numerals only, no commas or currency symbols, decimals are ok. Use styling for H1-H5, bold, italic. Each complex response should be returned separately, like ones with tables and text.\n\n## AGENT INSTRUCTIONS END ##`,
                  },
                ],
              },
              {
                role: "model",
                parts: [{ text: `Understood. I am now ${activeAgent.name}. I will follow the provided instructions.` }],
              },
              ...chatHistory,
            ];
          }

          let text;
          // if (window.location.host === "localhost:5173") {
          //   await new Promise((resolve) => setTimeout(resolve, 2000));
          //   text = "This is a simulated response from localhost. The backend was not called. \n\n You said: " + messageToSend;
          // } else {
            // Standard httpsCallable call
            const result = await callGeminiAPI({
                message: messageToSend,
                history: historyWithAgent || [],
                agent: activeAgent
                  ? {
                      name: activeAgent.name,
                      role: activeAgent.role,
                      inst: activeAgent.inst,
                    }
                  : null,
                file: file,
                useGoogleSearch: true
            });
            // console.log("Gemini API Response data:", result.data);
            text = result.data.response;

            // Increment token usage in Firestore
            if (result.data.usageMetadata && typeof result.data.usageMetadata.totalTokenCount === 'number') {
              const tokenCount = result.data.usageMetadata.totalTokenCount;
              try {
                const orgDocRef = doc(db, "orgs", "123test");
                await setDoc(orgDocRef, {
                  tokenUsage: increment(tokenCount)
                }, { merge: true });
                console.log(`Successfully incremented token usage for org 123test by ${tokenCount}.`);
              } catch (error) {
                console.error("Error updating token usage in Firestore:", error);
              }
            }
          // }

          // Add AI response
          setMessages((prevMessages) => [...prevMessages.filter(m => m.id !== "thinking-message"), { text: text, sender: "AI" }]);
          // console.log("sendMessage: Received response text:", text.substring(0, 200) + "...");

          // Update chat history
          const newHistory = [...chatHistory, { role: "user", parts: [{ text: messageToSend }] }, { role: "model", parts: [{ text }] }];

          const updatedChat = {
            ...(chatRef.current || effectiveChat),
            history: newHistory,
          };
          chatRef.current = updatedChat;
          setChat(updatedChat);

          setNewMessage("");
          setLatestResponse(text);
          // console.log("sendMessage: State updated successfully.");
          
          // await handleBuildCanvas(text); // Auto-build disabled per request

          setIsLoading(false);
          setIsSending(false);
          return text; // Exit the loop if successful
        } catch (error) {
          console.error(`sendMessage Error (attempt ${attempt}):`, error); // Log the full error object
          if (error.code) {
            console.error(`Firebase Error Code: ${error.code}`);
            console.error(`Firebase Error Details:`, error.details);
          }
          // Check for specific error types if needed (e.g., error.message, error.response)
          if (error.message && error.message.includes("quota")) {
            console.warn("sendMessage: Quota likely exceeded.");
          }

          if (attempt >= maxRetries) {
            console.error("sendMessage: Max retries reached. Aborting.");
            setIsLoading(false);
            setIsSending(false);
            // Just add the system error
            setMessages((prevMessages) => [
              ...prevMessages.filter(m => m.id !== "thinking-message"),
              {
                text: `Error: Could not get response after ${maxRetries} attempts. (${error.code || error.message})`,
                sender: "System",
              },
            ]);
            return; // Exit the loop after max retries
          }
          // console.log(`sendMessage: Waiting 2 seconds before retry ${attempt + 1}...`);
          await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait before retrying
        }
      }
    },
    [newMessage, handleBuildCanvas]
  );

  // const handleTableParsed = (jsonData) => {
  //   console.log("Parsed table data:", jsonData);
  // };

const aiIconSvg = `<span style="margin: 5px;"><svg height="24" width="24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M19.89,9.55A1,1,0,0,0,19,9H14V3a1,1,0,0,0-.69-1,1,1,0,0,0-1.12.36l-8,11a1,1,0,0,0-.08,1A1,1,0,0,0,5,15h5v6a1,1,0,0,0,.69.95A1.12,1.12,0,0,0,11,22a1,1,0,0,0,.81-.41l8-11A1,1,0,0,0,19.89,9.55ZM12,17.92V14a1,1,0,0,0-1-1H7l5-6.92V10a1,1,0,0,0,1,1h4Z"></path></svg></span>`;
const userIconSvg = `<span style="margin: 5px;"><svg height="24" width="24" xmlns="http://www.w3.org/2000/svg" id="user" x="0" y="0" enable-background="new 0 0 16 16" version="1.1" viewBox="0 0 16 16" xml:space="preserve"><path id="path7" stroke="none" d="M8 .986A7.022 7.022 0 0 0 .986 8c0 1.874.73 3.635 2.055 4.959A6.965 6.965 0 0 0 8 15.014 7.022 7.022 0 0 0 15.014 8 7.022 7.022 0 0 0 8 .986zm0 1A6.021 6.021 0 0 1 14.014 8a5.984 5.984 0 0 1-1.606 4.074 5.836 5.836 0 0 0-2.564-1.754 2.999 2.999 0 0 0 1.11-2.326A2.997 2.997 0 0 0 7.94 5.006a2.997 2.997 0 0 0-2.988 3.012c0 .929.436 1.75 1.104 2.298a5.846 5.846 0 0 0-2.526 1.698A5.964 5.964 0 0 1 1.986 8 6.021 6.021 0 0 1 8 1.986zm-.035 4.02c1.097 0 1.988.892 1.988 2.012A1.988 1.988 0 0 1 8.03 10c-.029 0-.057-.006-.086-.006-.025 0-.049.005-.074.006a1.994 1.994 0 0 1-1.916-2.006c0-1.096.892-1.988 2.012-1.988zm-.096 4.992c.024.001.048.008.072.008h.024c.022 0 .04-.007.062-.008a4.84 4.84 0 0 1 3.643 1.752A5.963 5.963 0 0 1 8 14.014a5.965 5.965 0 0 1-3.742-1.31 4.848 4.848 0 0 1 3.611-1.706z"></path></svg></span>`;

  // const getMessageHtml = (message) => {
  //   const messageHtml = marked(message.text);
  //   let content = message.text;
  //   const style =
  //     message.sender === "User"
  //       ? "background-color: #1C1E1F; color: #D9D9D9; margin: 8px; border-radius:12px; padding: 12px;"
  //       : "background-color: #272A2C; color: #D9D9D9; margin: 12px 0; padding: 12px;";

  //   if (message.sender === "AI") {
  //     content = aiIconSvg + content;
  //   } else {
  //     content = userIconSvg + content;
  //   }
  //   const iconSvg = message.sender === "AI" ? aiIconSvg : userIconSvg;

  //   return `<div style="${style}">${iconSvg}${messageHtml}</div>`;
  // };

  const handleInputChange = (event) => {
    setNewMessage(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!isLoading && !isSending) {
        sendMessage();
      }
    }
  };

  // const copyToClipboard = () => {
  //   const responseText = document.getElementById("responseText").innerText;
  //   navigator.clipboard
  //     .writeText(responseText)
  //     .then(() => {
  //       console.log("Text copied to clipboard");
  //     })
  //     .catch((err) => {
  //       console.error("Failed to copy text: ", err);
  //     });
  // };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isLoading && !isSending) {
      sendMessage();
    }
  };

  
  const handlePRDclick = () => {
    sendMessage("Create a Product Requirements Document for this data");
    setIsLoading(false); // Reset loading state
    setIsSending(false); // Reset sending state
    setIsResponseMinimized(true);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
        duration: 4000,
      });
      console.log("Scrolling main page to bottom after 2 seconds.");
    }, 2000);
  };

  const handleMPclick = () => {
    sendMessage("Create a Marketing Plan Document for this data");
    setIsLoading(false); // Reset loading state
    setIsSending(false); // Reset sending state

    setIsResponseMinimized(true);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
        duration: 4000,
      });
      console.log("Scrolling main page to bottom after 2 seconds.");
    }, 2000);
  };

      let localAllData = [];

    const handleExportImage = useCallback((blob, componentId) => {
      // const url = window.URL.createObjectURL(blob);
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = `component-${componentId}.pdf`;
      // document.body.appendChild(a);
      // a.click();
      // window.URL.revokeObjectURL(url);
      // a.remove();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64data = reader.result.split(',')[1];
    
          const payloadSizeMB = base64data.length / (1024 * 1024);
          console.log(`PDFtoPNG: Payload size is ${payloadSizeMB.toFixed(2)} MB`);
    
          if (!base64data) {
            console.error("Error calling PDFtoPNG: No PDF data to process. The export from the component may have been empty.");
            return resolve(null);
          }
    
          if (!currentUser) {
            console.error("Error calling PDFtoPNG: User not authenticated.");
            return resolve(null);
          }
    
          try {
            const token = await currentUser.getIdToken();
            const functionsUrl = `https://us-central1-${db.app.options.projectId}.cloudfunctions.net/PDFtoPNG`;
    
            const response = await fetch(functionsUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
              body: JSON.stringify({ pdfData: base64data }),
            });
    
            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(`PDFtoPNG function failed: ${errorData.error || response.statusText}`);
            }
    
            const result = await response.json();
    
            if (result.pngData) {
              resolve({
                componentId: componentId,
                type: "image",
                content: result.pngData
              });
            } else {
                resolve(null);
            }
          } catch (error) {
            console.error("Error calling PDFtoPNG function:", error);
            reject(error);
          }
        };
        reader.onerror = (error) => reject(error);
      });
    }, [currentUser]);
  
  const collectAllTableData = useCallback(async (type, selectedSlides) => {
    let comps = setComponents || [];
    
    // If selectedSlides is provided (it can be an empty array), we filter.
    // If it's undefined, we skip filtering for backward compatibility.
    if (typeof selectedSlides !== 'undefined' && viewId) {
        if (!selectedSlides || selectedSlides.length === 0) {
            comps = [];
        } else {
            try {
                const viewDocRef = doc(db, "views", viewId);
                const docSnap = await getDoc(viewDocRef);

                if (docSnap.exists()) {
                    const viewData = docSnap.data();
                    const allSlides = viewData.slides || [];
                    
                    const orderedSlides = selectedSlides.map(slideId => allSlides.find(s => s.id === slideId)).filter(Boolean);
                    const componentIdsInOrder = orderedSlides.flatMap(slide => slide.components);
                    
                    const componentMap = new Map((setComponents || []).map(c => [c.id, c]));
                    comps = componentIdsInOrder.map(id => componentMap.get(id)).filter(Boolean);

                } else {
                    console.warn("View document not found, cannot filter components by slide.");
                    comps = [];
                }
            } catch (error) {
                console.error("Error fetching slides for filtering:", error);
                comps = []; // Clear components on error to prevent exporting all
            }
        }
    }
    
    if (type === "export") {
        const dataPromises = comps.map(component => {
            if (component.type === 'data-visualization') {
                const componentId = component.id;
                const tableToolbar = tableToolbarRefs.current.get(componentId);
                if (tableToolbar) {
                    const report = tableToolbar.getReport();
                    const viewType = (report && report.options && report.options.viewType) ? report.options.viewType : 'grid';

                    if (viewType === 'grid' || viewType === 'flat') {
                        // Ignore image export for grid and flat views
                        return Promise.resolve(null);
                    }

                    return new Promise((resolve) => {
                        tableToolbar.exportTo("PDF", { destinationType: "plain", pageOrientation: "landscape" }, (result) => {
                            // console.log(`[GeminiComponent] Flexmonster exportTo callback for component ${componentId}.`);
                            // console.log('[GeminiComponent] Full result object:', result);

                            if (result && result.data) {
                                // For PDF with "plain" destination, result.data should be a jsPDF object.
                                // console.log(`[GeminiComponent] result.data type: ${typeof result.data}`);
                                
                                // Check if it has the 'output' method, characteristic of a jsPDF object.
                                if (typeof result.data.output === 'function') {
                                    // console.log('[GeminiComponent] result.data appears to be a jsPDF object.');
                                    try {
                                        const jsPdfDoc = result.data;
                                        jsPdfDoc.setFont('Helvetica'); 
                                        const pageCount = jsPdfDoc.internal.getNumberOfPages();
                                        if (pageCount > 1) {
                                            for (let i = pageCount; i > 1; i--) {
                                                jsPdfDoc.deletePage(i);
                                            }
                                        }
                                        // Use the jsPDF object's output method to get a Blob
                                        const blob = jsPdfDoc.output('blob');
                                        // console.log(`[GeminiComponent] Converted jsPDF object to blob. Blob size: ${blob.size}`);

                                        if (blob.size > 0) {
                                            // handleExportImage takes the blob, reads it as a data URL, 
                                            // extracts the base64 part, and sends it to the cloud function.
                                            resolve(handleExportImage(blob, componentId));
                                        } else {
                                            // console.error(`PDF export resulted in an empty blob for component ${componentId}.`);
                                            resolve(null);
                                        }
                                    } catch (e) {
                                        // console.error(`Error processing jsPDF object for component ${componentId}:`, e);
                                        resolve(null);
                                    }
                                } else {
                                    //  console.error(`Export from Flexmonster for PDF was not the expected jsPDF object for component ${componentId}.`);
                                    //  console.log(`[GeminiComponent] Received problematic result.data for component ${componentId}:`, result.data);
                                     resolve(null);
                                }
                            } else {
                                // console.error(`Export from Flexmonster is empty or has no data for component ${componentId}.`);
                                // console.log(`[GeminiComponent] Received problematic result for component ${componentId}:`, result);
                                resolve(null);
                            }
                        });
                    });
                }
            } else if (component.type === 'text-editor') {
                return Promise.resolve({
                  cards: component.cards,
                  componentId: component.id,
                  type: component.type,
                  content: component.content
                });
            }
            return Promise.resolve(null);
        });

        const allData = await Promise.all(dataPromises);
        return allData.filter(Boolean);

    } else if (type === "analyze") {
        const allData = [];
        for (const component of comps) {
            if (component.type === 'data-visualization') {
                try {
                    const config = JSON5.parse(component.content);
                    let componentData = null;
                    if (config && config.dataSource && config.dataSource.type === "json") {
                        if (config.dataSource.data) {
                            componentData = config.dataSource.data;
                        } else if (config.dataSource.filename) {
                            const response = await axios.get(config.dataSource.filename);
                            componentData = response.data;
                        }
                    } else if (config && config.dataSource && config.dataSource.type === "csv") {
                        if (config.dataSource.filename) {
                            const response = await axios.get(config.dataSource.filename);
                            componentData = await CSV().fromString(response.data);
                        }
                    }
                    if (componentData) {
                        allData.push({
                            cards: component.cards,
                            componentId: component.id,
                            type: component.type,
                            content: componentData
                        });
                    }
                } catch (error) {
                  console.error("Error parsing component content for analysis:", error);
                }
            } else if (component.type === 'text-editor') {
                allData.push({
                  cards: component.cards,
                  componentId: component.id,
                  type: component.type,
                  content: component.content
                });
            }
        }
        return allData;
    }
    return [];
  }, [setComponents, handleExportImage, viewId]);

     const loadDataIntoHistory = useCallback(async () => {
    const allTableData = await collectAllTableData("analyze");
    
    if (allTableData.length === 0) {
      return;
    }

    // Update the ref so handleAnalyzeClick knows this data is already in history
    lastSentDataRef.current = JSON.stringify(allTableData);

    setChat((prev) => ({
      ...prev,
      history: [
        ...(prev?.history || []),
        { role: "user", parts: [{ text: `Here is the data for the page: ${JSON.stringify(allTableData, null, 2)}` }] },
        { role: "model", parts: [{ text: "I have received the data." }] }
      ]
    }));
  }, [collectAllTableData]);


  const handleAnalyzeClick = useCallback(async (shouldOpen = true, agent = null) => {
    const predefinedQuestion = "Analyze this page.";
    const allTableData = await collectAllTableData("analyze");
    // console.log('Analyze button clicked : ', allTableData);
    if (shouldOpen) setIsResponseMinimized(false);

    if (allTableData.length === 0) {
      console.error("No table data available for analysis.");
      return;
    }

    const dataString = JSON.stringify(allTableData);
    // Prevent re-sending the same data if it's already in the history (and history isn't empty)
    if (chat?.history?.length > 0 && lastSentDataRef.current === dataString) {
      console.log("Data already analyzed and unchanged. Skipping re-analysis prompt.");
      return;
    }
    lastSentDataRef.current = dataString;
    
    // Create the prompt with the data and the predefined question
    const prompt = `${predefinedQuestion} 
    \n\n
    ${JSON.stringify(allTableData, null, 2)}`;

    // Send the prompt to Gemini using the sendMessage function
    // console.log('Sending analysis prompt:', prompt);
    return await sendMessage(prompt, { ensureOpen: shouldOpen, agent: agent });
    // getActiveFlexmonsterInstances();
  }, [collectAllTableData, sendMessage, chat?.history]);

  // --- THIS useEffect handles AGENT SWITCHING --- \\
  useEffect(() => {
    // Only run if currentAgent changes AND chat is ready
    // Check if we already initialized this agent to prevent loops if dependencies change
    if (currentAgent && chat?.ready) {
      const performInitialAnalysis = async () => {
        if (isSequenceRunningRef.current) return;
        isSequenceRunningRef.current = true;
        setIsAgentRunning(true);
        try {
          if (isAgentMode) {
            const text = await handleAnalyzeClick(true, currentAgent);
            if (text) {
              await handleBuildCanvas(text);
              if (currentDoc) {
                await handleDocSelectionFromSelector(currentDoc);
                toggleAgentMode(false);
              }
              isInitialLoadRef.current = false;
            }
          } else {
            await loadDataIntoHistory();
            isInitialLoadRef.current = false;
          }
        } finally {
          isSequenceRunningRef.current = false;
          setIsAgentRunning(false);
        }
      };

      if (currentAgent.id !== lastInitializedAgentIdRef.current) {
        lastInitializedAgentIdRef.current = currentAgent.id;

        // Define an async function to handle the agent switch
        const startNewAgentChat = async () => {
          setIsLoading(true); // Indicate loading during switch
          const loadingText = `Loading agent to: ${currentAgent.name}...`;
          setMessages((prevMessages) => [...prevMessages, { text: loadingText, sender: "System" }]); // Notify user

          try {
            // Initialize chat with new agent instructions via Firebase function
            let result;
            // if (window.location.host === "localhost:5173") {
            //   result = { data: { success: true } };
            // } else {
            result = await initializeGeminiChat({
              agentInstructions: {
                name: currentAgent.name,
                role: currentAgent.role,
                instructions: currentAgent.inst,
              },
            });
            // }

            if (result.data.success) {
              // Update the chat state to include agent info
              setChat({
                ready: true,
                type: "firebase",
                currentAgent: currentAgent,
                history: [], // Reset history for new agent
              });
              console.log("New chat session started successfully for agent:", currentAgent.name);

              // Clear messages and add greeting for new agent
              // setMessages((prev) => [...prev, { text: `${currentAgent.name}: How can I help?`, sender: "AI" }]);
            }
          } catch (error) {
            console.error("Error starting new chat session for agent:", currentAgent.name, error);
            // Handle the error (e.g., show error message in chat)
            setMessages((prevMessages) => [
              ...prevMessages,
              { text: `Error switching to agent ${currentAgent.name}: ${error.message}`, sender: "System" },
            ]);
          } finally {
            setMessages((prev) => prev.filter((msg) => msg.text !== loadingText));
            setIsLoading(false); // Stop loading indicator
            if (isInitialLoadRef.current) {
              await performInitialAnalysis();
            } else {
              await handleAnalyzeClick(true, currentAgent);
            }
          }
        };

        startNewAgentChat(); // Execute the async function
      } else if (isInitialLoadRef.current && isAgentMode && setComponents && setComponents.length > 0) {
        // Retry initial analysis if data arrived late
        performInitialAnalysis();
      }
    }
    // Dependency array: Runs when currentAgent changes
  }, [currentAgent, chat?.ready, handleAnalyzeClick, loadDataIntoHistory, setComponents, currentDoc]);

    //////////////////////////////
  // -- Switch Docs -- //
  //////////////////////////////

  // Stable function for saving selected doc ID to the 'views' collection
  const saveLastSelectedDocIdToView = useCallback(
    async (docId) => {
      if (!viewId || !currentUser?.uid) {
        console.error("Cannot save last selected doc ID: viewId or currentUser.uid is missing.");
        return;
      }
      try {
        const viewDocRef = doc(db, "views", viewId);
        await updateDoc(viewDocRef, {
          lastSelectedDocId: docId,
          lastSelectedDocTimestamp: new Date(), // Optional: for tracking
          // editedAt: new Date(),
          // editedBy: currentUser.uid,
        });
        console.log(`Saved last selected doc ID (${docId}) to 'views' collection for view ${viewId}.`);
      } catch (error) {
        console.error("Error saving last selected doc ID to Firebase:", error);
      }
    },
    [viewId, currentUser?.uid]
  );
  
  // Stable handler for when DocSelector triggers an doc selection
  const handleDocSelectionFromSelector = useCallback(
    async (docData) => {
      console.log("GeminiComponent: Doc selected via DocSelector", docData);
      setCurrentDoc(docData); // Update main state

      // GeminiComponent saves the selection to the 'views' doc.
      // DocSelector itself saves to 'users.{uid}.docCurrent' upon click.
      if (docData && docData.id) {
        saveLastSelectedDocIdToView(docData.id);
      }
      
      if (!lastSentDataRef.current) {
        console.log("No data in chat history, collecting now...");
        await loadDataIntoHistory();
      }

      setIsResponseMinimized(false);
      const text = await sendMessage("Use this data to create a " + docData.name + " " + docData.description + " using this as a template: " + docData.content);
      if (text) {
        await handleBuildCanvas(text);
      }
      
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
          duration: 4000,
        });
        console.log("Scrolling main page to bottom after 2 seconds.");
      }, 2000);
    },
    [saveLastSelectedDocIdToView, sendMessage, handleBuildCanvas, loadDataIntoHistory]
  ); // Depends on the memoized save function

  // Stable function to load initial doc based on users.{uid}.docCurrent
  const loadInitialDocFromUserDoc = useCallback(async () => {
    if (!currentUser?.uid || !availableDocs || availableDocs.length === 0) {
      console.log("Cannot load initial doc: missing user, or no available docs.");
      return;
    }
    // Load the last selected doc ID from the user's document
    const userDocRef = doc(db, "users", currentUser.uid);
    const docSnap = await getDoc(userDocRef);
    let docToSet = availableDocs[0]; // Default to the first doc

    if (docSnap.exists() && docSnap.data().docCurrent) {
      // Check for the 'docCurrent' field
      const lastId = docSnap.data().docCurrent; // Get the doc ID from 'docCurrent'
      const foundDoc = availableDocs.find((doc) => doc.id === lastId);
      if (foundDoc) docToSet = foundDoc; // If found, use the saved agent
    }
    setCurrentDoc(docToSet);
    console.log("Initial doc set to:", docToSet?.title || "first available");
    console.log("Initial doc set to:", docToSet?.name || "first available");
  }, [currentUser?.uid, availableDocs]); // Depends on user and the list of agents

  // Effect to load the initial agent once availableDocs list is populated
  useEffect(() => {
    if (currentUser?.uid && availableDocs.length > 0) {
      loadInitialDocFromUserDoc();
    }
  }, [currentUser?.uid, availableDocs, loadInitialDocFromUserDoc]); // Correct dependencies


  const sendToGDocs = async (content) => {
    // if (!content) {
    //   setMessages((prev) => [...prev, { text: "No content available to send to Google Docs.", sender: "System" }]);
    //   return;
    // }

    const documentTitle = `DataSculpture AI - Generated Document (${new Date().toLocaleDateString()})`;
    const documentContent = marked(content);

    setMessages((prev) => [...prev, { text: `Creating Google Doc: "${documentTitle}"...`, sender: "System" }]);

    try {
      let token = googleAuthToken;
      if (!token) {
        console.log("Requesting OAuth token for Google Docs creation...");
        token = await getOAuthToken();
        setGoogleAuthToken(token);
        console.log("OAuth token acquired for Google Docs creation.");
      }

      const metadata = {
        name: documentTitle,
        mimeType: "application/vnd.google-apps.document",
      };

      const form = new FormData();
      form.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
      form.append("file", new Blob([documentContent], { type: "text/html" }));

      let response = await fetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: form,
      });

      if (response.status === 401) {
        console.log("Token expired, refreshing...");
        token = await getOAuthToken();
        setGoogleAuthToken(token);

        response = await fetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: form,
        });
      }

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(`API Error: ${errorBody.error.message}`);
      }

      const file = await response.json();
      const docLink = `https://docs.google.com/document/d/${file.id}/edit`;
      const successMessage = `Successfully created Google Doc: "${file.name}". You can view it here: ${docLink}`;
      setMessages((prev) => [...prev, { text: successMessage, sender: "System" }]);
      window.open(docLink, "_blank"); // Open the new doc in a new tab
    } catch (error) {
      console.error("Error in sendToGDocs:", error);
      setMessages((prev) => [...prev, { text: `Error creating Google Doc: ${error.message}`, sender: "System" }]);
    }
  };

  const uploadImageForSlide = useCallback(async (base64Image, componentId) => {
    const storageRef = firebaseRef(storage, `temp_slides_images/${componentId}_${Date.now()}.png`);
    console.log(` Firebase Storage at ${storageRef.fullPath}...`);
    try {
      const snapshot = await uploadString(storageRef, base64Image, 'base64', {
        contentType: 'image/png'
      });
      const downloadURL = await getDownloadURL(snapshot.ref);
      return { downloadURL, storagePath: snapshot.ref.fullPath };
    } catch (error) {
      console.error("Error uploading image to Firebase Storage:", error);
      return null;
    }
  }, []);

  const sendToGSlides = async (requests, imagePathsToDelete) => {
    const presentationTitle = `DataSculpture AI - Generated Presentation (${new Date().toLocaleDateString()})`;

    setMessages((prev) => [...prev, { text: `Creating Google Slides: "${presentationTitle}"...`, sender: "System" }]);

    try {
      let token = googleAuthToken;
      if (!token) {
        token = await getOAuthToken();
        setGoogleAuthToken(token);
      }

      let response = await fetch("https://slides.googleapis.com/v1/presentations", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title: presentationTitle }),
      });

      if (response.status === 401) {
        token = await getOAuthToken();
        setGoogleAuthToken(token);
        response = await fetch("https://slides.googleapis.com/v1/presentations", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ title: presentationTitle }),
        });
      }

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(`API Error: ${errorBody.error.message}`);
      }

      const presentation = await response.json();
      const presentationId = presentation.presentationId;

      if (requests.length > 0) {
        const batchUrl = `https://slides.googleapis.com/v1/presentations/${presentationId}:batchUpdate`;
        const batchResponse = await fetch(batchUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ requests }),
        });

        if (!batchResponse.ok) {
          const errorBody = await batchResponse.json();
          console.error("Slides batchUpdate failed:", errorBody);
          // Even if update fails, try to clean up images
          if (imagePathsToDelete && imagePathsToDelete.length > 0) {
            console.log("Cleaning up temporary images after failed update...");
            const deletePromises = imagePathsToDelete.map(path => {
              const imageRef = firebaseRef(storage, path);
              return deleteObject(imageRef).catch(err => console.error(`Failed to delete temp image: ${path}`, err));
            });
            await Promise.all(deletePromises);
          }
          throw new Error(`Failed to add content to slides: ${errorBody.error.message}`);
        }
      }

      // Cleanup temporary images after successful update
      if (imagePathsToDelete && imagePathsToDelete.length > 0) {
        console.log("Cleaning up temporary images...");
        const deletePromises = imagePathsToDelete.map(path => {
          const imageRef = firebaseRef(storage, path);
          return deleteObject(imageRef).catch(err => console.error(`Failed to delete temp image: ${path}`, err));
        });
        await Promise.all(deletePromises);
        console.log("Cleanup complete.");
      }

      const slidesLink = `https://docs.google.com/presentation/d/${presentationId}/edit`;
      const successMessage = `Successfully created Google Slides: "${presentation.title}". You can view it here: ${slidesLink}`;
      setMessages((prev) => [...prev, { text: successMessage, sender: "System" }]);
      window.open(slidesLink, "_blank");
    } catch (error) {
      console.error("Error in sendToGSlides:", error);
      setMessages((prev) => [...prev, { text: `Error creating Google Slides: ${error.message}`, sender: "System" }]);
    }
  };

  const exportToGDocs = async (selectedSlides) => {
    const allData = await collectAllTableData("export", selectedSlides);
    console.log("exportToGDocs allData:", allData);
    
    const htmlContent = allData.map(item => {
        if (item.type === 'text-editor') {
            return item.content;
        }
        if (item.type === 'image') {
            return `<img src="data:image/png;base64,${item.content}" width="650px" />`;
        }
        // for analyze data
        if (item.type === 'data-visualization') {
            return `<div><h3>Data Visualization (${item.componentId})</h3><pre>${JSON.stringify(item.content, null, 2)}</pre></div>`;
        }
        return '';
    }).join('<br/>');

    if (htmlContent) {
        await sendToGDocs(htmlContent);
    } else {
        console.log("No content to export to Google Docs.");
    }
  };

    const exportToConfluence = async (selectedSlides) => {
    if (!authUser) {
      setMessages((prev) => [...prev, { text: "Please sign in to continue.", sender: "System" }]);
      return;
    }

    if (atlassianAuthStatus === 'unknown') {
      setMessages((prev) => [...prev, { text: "Checking Atlassian connection status, please wait...", sender: "System" }]);
      return;
    }

    if (atlassianAuthStatus === 'disconnected') {
      setAtlassianService('Confluence');
      setIsAtlassianConnectModalOpen(true);
      return;
    }

    // --- Proceed if connected ---
    setMessages((prev) => [...prev, { text: "Fetching Confluence spaces...", sender: "System" }]);
    try {
      const result = await getConfluenceSpacesCallable();
      const spaces = result.data;
      if (!spaces || spaces.length === 0) {
        setMessages((prev) => [...prev, { text: "Error: No Confluence spaces found for your account.", sender: "System" }]);
        return;
      }
      setConfluenceSpaces(spaces);
      setIsConfluenceModalOpen(true);
    } catch (error) {
      if (error.code === 'failed-precondition') {
        setAtlassianAuthStatus('disconnected');
        setAtlassianService('Confluence');
        setIsAtlassianConnectModalOpen(true);
      } else {
        console.error("Error getting Confluence spaces:", error);
        setMessages((prev) => [...prev, { text: `Error getting Confluence spaces: ${error.message}`, sender: "System" }]);
      }
    }
  };

  const handleCreateConfluencePage = async () => {
    if (!selectedConfluenceSpace) {
      setMessages((prev) => [...prev, { text: "Please select a Confluence space.", sender: "System" }]);
      return;
    }

    const allData = await collectAllTableData("export", []);
    const htmlContent = allData.map(item => {
        if (item.type === 'text-editor') return item.content;
        if (item.type === 'image') return `<img src="data:image/png;base64,${item.content}" width="650px" />`;
        if (item.type === 'data-visualization') return `<div><h3>Data Visualization (${item.componentId})</h3><pre>${JSON.stringify(item.content, null, 2)}</pre></div>`;
        return '';
    }).join('<br/>');

    if (htmlContent) {
        setMessages((prev) => [...prev, { text: "Creating Confluence page...", sender: "System" }]);
        try {
            const result = await createConfluencePageCallable({
                title: "New Page from DataSculpture",
                content: htmlContent,
                spaceId: selectedConfluenceSpace,
            });

            if (result.data.success && result.data.url) {
                const pageUrl = result.data.url;
                const pageTitle = result.data.title;
                const messageText = `Successfully created Confluence page: [${pageTitle}](${pageUrl})`;
                setMessages((prev) => [...prev, { text: messageText, sender: "System" }]);
            } else {
                setMessages((prev) => [...prev, { text: `Failed to create Confluence page.`, sender: "System" }]);
            }
            setIsConfluenceModalOpen(false);
        } catch (error) {
            console.error("Error creating Confluence page:", error);
            setMessages((prev) => [...prev, { text: `Error creating Confluence page: ${error.message}`, sender: "System" }]);
        }
    } else {
        console.log("No content to export to Confluence.");
        setMessages((prev) => [...prev, { text: "No content to export.", sender: "System" }]);
    }
  };


  const exportToHubspot = async (selectedSlides) => {
    if (!mergeAccountToken) {
      handleMergeLink(['ticketing']);
      setMessages((prev) => [...prev, { text: "Please link your Hubspot account first.", sender: "System" }]);
      return;
    }

    const allData = await collectAllTableData("export", selectedSlides);
    console.log("exportToHubspot allData:", allData);
    
    const description = allData.map(item => {
        if (item.type === 'text-editor') {
            return item.content;
        }
        if (item.type === 'image') {
            return `Image of chart ${item.componentId}`;
        }
        if (item.type === 'data-visualization') {
            return `Data Visualization (${item.componentId}): ${JSON.stringify(item.content, null, 2)}`;
        }
        return '';
    }).join('\n\n');

    if (description) {
      try {
        const result = await createMergeTicket({
          name: "New Ticket from DataSculpture",
          description: description,
          collections: [],
          accountToken: mergeAccountToken,
        });
        console.log("Merge ticket created:", result.data);
        setMessages((prev) => [...prev, { text: `Successfully created ticket.`, sender: "System" }]);
      } catch (error) {
        console.error("Error creating merge ticket:", error);
        setMessages((prev) => [...prev, { text: `Error creating ticket: ${error.message}`, sender: "System" }]);
      }
    } else {
        console.log("No content to export to Hubspot.");
    }
  };

  const exportToJira = async (selectedSlides) => {
    setJiraExportSlides(selectedSlides);
    if (!authUser) {
      setMessages((prev) => [...prev, { text: "Please sign in to continue.", sender: "System" }]);
      return;
    }

    if (atlassianAuthStatus === 'unknown') {
      setMessages((prev) => [...prev, { text: "Checking Atlassian connection status, please wait a moment...", sender: "System" }]);
      return;
    }

    if (atlassianAuthStatus === 'disconnected') {
      setAtlassianService('Jira');
      setIsAtlassianConnectModalOpen(true);
      return;
    }

    // --- Proceed if connected ---
    setMessages((prev) => [...prev, { text: "Connecting to Jira and creating issue...", sender: "System" }]);

    try {
      const result = await getJiraProjectsCallable();
      const projects = result.data;

      if (!projects || projects.length === 0) {
        setMessages((prev) => [...prev, { text: "Error: No Jira projects found for your account.", sender: "System" }]);
        return;
      }

      setJiraProjects(projects);
      setIsJiraModalOpen(true);

    } catch (error) {
        if (error.code === 'failed-precondition') {
          setAtlassianAuthStatus('disconnected');
          setAtlassianService('Jira');
          setIsAtlassianConnectModalOpen(true);
        } else {
          console.error("Error during Jira export:", error);
          setMessages((prev) => [...prev, { text: `Error creating Jira issue: ${error.message}`, sender: "System" }]);
        }
    }
  };

  const handleJiraProjectChange = (projectId) => {
    setSelectedJiraProject(projectId);
    // Reset epic and sprint selection as they are not used for now
    setSelectedJiraEpic(null);
    setJiraEpics([]);
    setSelectedJiraSprint(null);
    setJiraSprints([]);
  };

  const handleCreateJiraIssue = async () => {
    setIsJiraModalOpen(false); // Close selection modal immediately
    setIsExporting(true); // Show loading modal from NavBar

    const allData = await collectAllTableData("export", jiraExportSlides);

    if (!allData || allData.length === 0) {
      console.log("No content to export to Jira.");
      setMessages((prev) => [...prev, { text: "No content to export to Jira.", sender: "System" }]);
      setIsExporting(false);
      return;
    }

    const convertHtmlToAdf = (html) => {
      const adf = {
        version: 1,
        type: "doc",
        content: [],
      };
      let foundTitle = null;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html.trim();

      let titleNode = null;
      for (const child of tempDiv.children) {
        if (child.textContent.trim()) {
          titleNode = child;
          break;
        }
      }

      if (titleNode) {
        foundTitle = titleNode.textContent.trim();
        titleNode.remove();
      }

      tempDiv.childNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
              const nodeName = node.nodeName.toLowerCase();
              if (nodeName.match(/^h[1-3]$/)) {
                  const level = parseInt(nodeName.substring(1), 10);
                  if(node.textContent.trim()){
                    adf.content.push({
                        type: "heading",
                        attrs: { level },
                        content: [{ type: "text", text: node.textContent.trim() }]
                    });
                  }
              } else if (nodeName === 'p') {
                  if(node.textContent.trim()){
                    adf.content.push({
                        type: "paragraph",
                        content: [{ type: "text", text: node.textContent.trim() }]
                    });
                  }
              } else if (nodeName === 'ul' || nodeName === 'ol') {
                  const listItems = [];
                  const items = node.querySelectorAll('li');
                  items.forEach(li => {
                      if(li.textContent.trim()){
                        listItems.push({
                            type: "listItem",
                            content: [{
                                type: "paragraph",
                                content: [{ type: "text", text: li.textContent.trim() }]
                            }]
                        });
                      }
                  });

                  if (listItems.length > 0) {
                      adf.content.push({
                          type: nodeName === 'ul' ? 'bulletList' : 'orderedList',
                          content: listItems
                      });
                  }
              } else if (node.textContent.trim()) {
                adf.content.push({
                    type: "paragraph",
                    content: [{ type: "text", text: node.textContent.trim() }]
                });
              }
          } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
              adf.content.push({
                  type: "paragraph",
                  content: [{ type: "text", text: node.textContent.trim() }]
              });
          }
      });

      return { adf, title: foundTitle };
    };

    for (const componentData of allData) {
      let description; // This will now be an ADF object
      let title = `New ticket for component ${componentData.componentId}`;

      if (componentData.type === 'text-editor') {
        const htmlContent = componentData.content;
        const { adf, title: foundTitle } = convertHtmlToAdf(htmlContent);
        description = adf;

        if (foundTitle) {
          title = foundTitle;
        }
      } else if (componentData.type === 'image') {
        description = {
          version: 1,
          type: 'doc',
          content: [{
            type: 'paragraph',
            content: [{
              type: 'text',
              text: `[Attached image of chart ${componentData.componentId}]`,
            }],
          }],
        };
        title = `Chart: ${componentData.componentId}`;
      } else {
        console.log(`Skipping component of unhandled type for Jira export: ${componentData.type}`);
        continue;
      }

      if (description && description.content.length > 0) {
        try {
          const issueResult = await createJiraIssueCallable({
            title: title,
            content: description,
            projectId: selectedJiraProject,
            epicId: null, 
            sprintId: null,
          });

          if (issueResult.data.success) {
            const issueUrl = `https://${issueResult.data.siteUrl}/browse/${issueResult.data.issue.key}`;
            const messageText = `Successfully created Jira issue: [${issueResult.data.issue.key}](${issueUrl})`;
            setMessages((prev) => [...prev, { text: messageText, sender: "System" }]);
          } else {
             setMessages((prev) => [...prev, { text: `Failed to create Jira issue. Details: ${JSON.stringify(issueResult.data)}`, sender: "System" }]);
          }
        } catch (error) {
            console.error("Error creating Jira issue:", error);
            const errorMessage = error.details?.message || error.message;
            setMessages((prev) => [...prev, { text: `Error creating Jira issue: ${errorMessage}`, sender: "System" }]);
        }
      } else {
          console.log("No content to export to Jira for component:", componentData.componentId);
      }
    }
    
    setIsExporting(false); // Hide loading modal
  };

  const exportToLinear = async (selectedSlides) => {
    setLinearExportSlides(selectedSlides);
    if (!authUser) {
      setMessages((prev) => [...prev, { text: "Please sign in to continue.", sender: "System" }]);
      return;
    }

    if (linearAuthStatus === 'unknown') {
      setMessages((prev) => [...prev, { text: "Checking Linear connection status, please wait a moment...", sender: "System" }]);
      return;
    }

    if (linearAuthStatus === 'disconnected') {
      setIsLinearConnectModalOpen(true);
      return;
    }

    setMessages((prev) => [...prev, { text: "Connecting to Linear and creating issue...", sender: "System" }]);

    try {
      const result = await getLinearTeamsCallable();
      const teams = result.data;

      if (!teams || teams.length === 0) {
        setMessages((prev) => [...prev, { text: "Error: No Linear teams found for your account.", sender: "System" }]);
        return;
      }

      setLinearTeams(teams);
      setIsLinearModalOpen(true);

    } catch (error) {
        if (error.code === 'failed-precondition') {
          setLinearAuthStatus('disconnected');
          setIsLinearConnectModalOpen(true);
        } else {
          console.error("Error during Linear export:", error);
          setMessages((prev) => [...prev, { text: `Error creating Linear issue: ${error.message}`, sender: "System" }]);
        }
    }
  };

  const handleCreateLinearIssue = async () => {
    setIsLinearModalOpen(false);
    setIsExporting(true);

    const allData = await collectAllTableData("export", linearExportSlides);

    if (!allData || allData.length === 0) {
      console.log("No content to export to Linear.");
      setMessages((prev) => [...prev, { text: "No content to export to Linear.", sender: "System" }]);
      setIsExporting(false);
      return;
    }
    const turndownService = new TurndownService();

    const extractTitleAndContent = (html) => {
      let title = null;
      let content = html.trim();
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      let firstElement = null;
      for (const child of tempDiv.children) {
        if (child.textContent.trim()) {
            firstElement = child;
            break;
        }
      }

      if (firstElement) {
        const potentialTitle = firstElement.textContent.trim();
        if (potentialTitle) {
          title = potentialTitle;
          firstElement.remove();
          content = tempDiv.innerHTML;
        }
      }
      
      return { title, content };
    };

    for (const componentData of allData) {
      let description = '';
      let title = `New ticket for component ${componentData.componentId}`;

      if (componentData.type === 'text-editor') {
          const { title: foundTitle, content: remainingContent } = extractTitleAndContent(componentData.content);
          if (foundTitle) {
            title = foundTitle;
          }
          description = remainingContent;
      } else if (componentData.type === 'image') {
          description = `[Attached image of chart ${componentData.componentId}]`;
          title = `Chart: ${componentData.componentId}`;
      } else {
         console.log(`Skipping component of unhandled type for Linear export: ${componentData.type}`);
         continue;
      }

      if (description) {
          try {
              const markdownDescription = turndownService.turndown(description);
              const issueResult = await createLinearIssueCallable({
                title: title,
                description: markdownDescription,
                teamId: selectedLinearTeam,
              });

              if (issueResult.data.success) {
                const messageText = `Successfully created Linear issue: ${issueResult.data.issue.title}`;
                setMessages((prev) => [...prev, { text: messageText, sender: "System" }]);
              } else {
                 setMessages((prev) => [...prev, { text: `Failed to create Linear issue.`, sender: "System" }]);
              }
          } catch (error) {
              console.error("Error creating Linear issue:", error);
              const errorMessage = error.details?.message || error.message;
              setMessages((prev) => [...prev, { text: `Error creating Linear issue: ${errorMessage}`, sender: "System" }]);
          }
      } else {
          console.log("No content to export to Linear for component:", componentData.componentId);
      }
    }
    
    setIsExporting(false);
  };


  const exportToGSlides = async (selectedSlides) => {
    const allData = await collectAllTableData("export", selectedSlides);
    console.log("exportToGSlides allData:", allData);

    const requests = [];
    const imagePathsToDelete = [];
    let slideCounter = 0;

    for (const item of allData) {
      if (item.type === 'text-editor') {
        const htmlContent = item.content;
        // Split content by H1/H2/H3 tags to create new slides
        const slides = htmlContent.split(/(<h[1-3][^>]*>.*?<\/h[1-3]>)/i);

        for (let i = 0; i < slides.length; i++) {
          const part = slides[i];
          if (!part.trim()) continue;

          const slideId = `slide_${item.componentId}_${slideCounter++}`;
          requests.push({ createSlide: { objectId: slideId, slideLayoutReference: { predefinedLayout: "BLANK" } } });
          
          let title = "";
          let body = part;

          const headerMatch = part.match(/<h[1-3][^>]*>(.*?)<\/h[1-3]>/i);
          if (headerMatch) {
            title = headerMatch[1];
            // If there's more content in this chunk, it becomes the body
            body = String(slides[i+1] || '').replace(/<[^>]+>/g, '').trim();
            i++; // Increment to skip the next part as it's now the body
          } else {
            body = part.replace(/<[^>]+>/g, '').trim();
          }

          if (title) {
            const titleId = `title_${slideId}`;
            requests.push({ createShape: { objectId: titleId, shapeType: "TEXT_BOX", elementProperties: { pageObjectId: slideId, size: { height: { magnitude: 50, unit: "PT" }, width: { magnitude: 600, unit: "PT" } }, transform: { scaleX: 1, scaleY: 1, translateX: 50, translateY: 20, unit: "PT" } } } });
            requests.push({ insertText: { objectId: titleId, text: title } });
          }

          if (body) {
             const bodyId = `body_${slideId}`;
            requests.push({ createShape: { objectId: bodyId, shapeType: "TEXT_BOX", elementProperties: { pageObjectId: slideId, size: { height: { magnitude: 300, unit: "PT" }, width: { magnitude: 600, unit: "PT" } }, transform: { scaleX: 1, scaleY: 1, translateX: 50, translateY: title ? 80 : 50, unit: "PT" } } } });
            requests.push({ insertText: { objectId: bodyId, text: body } });
          }
        }

      } else if (item.type === 'image') {
        const uploadResult = await uploadImageForSlide(item.content, item.componentId);
        if (uploadResult) {
          imagePathsToDelete.push(uploadResult.storagePath);
          const slideId = `slide_${item.componentId}_${slideCounter++}`;
          const imageId = `image_${slideId}`;
          requests.push({ createSlide: { objectId: slideId, slideLayoutReference: { predefinedLayout: "BLANK" } } });
          requests.push({
            createImage: {
              objectId: imageId,
              url: uploadResult.downloadURL,
              elementProperties: {
                pageObjectId: slideId,
                size: { height: { magnitude: 350, unit: "PT" }, width: { magnitude: 500, unit: "PT" } },
                transform: { scaleX: 1, scaleY: 1, translateX: 100, translateY: 50, unit: "PT" },
              }
            }
          });
        }
      }
    }

    if (requests.length > 0) {
      await sendToGSlides(requests, imagePathsToDelete);
    } else {
      console.log("No content to export to Google Slides.");
    }
  };

  // const handlePredefinedQuestion = () => {
  //   sendMessage(prompt);
  // };

  // useEffect(() => {
  //   const handleKeyPress = (e) => {
  //     if (e.key === "Enter") {
  //       // sendMessage();
  //     }
  //   };

  //   window.addEventListener("keypress", handleKeyPress);

  //   return () => {
  //     window.removeEventListener("keypress", handleKeyPress);
  //   };
  // }, []);

  // const handleFileChange = (event) => {
  //   console.log("Files selected:", event.target.files);
  //   setUploadedFiles(event.target.files);
  // };

  // const handleSearchChange = (event) => {
  //   console.log("Search string changed:", event.target.value);
  //   setSearchString(event.target.value);
  // };

  // const handleSearchSubmit = (event) => {
  //   event.preventDefault();
  //   setSearchString("Analyze this page: ");
  //   // console.log('Search submitted with string:', searchString);
  //   sendMessage(searchString);
  // };

  // const handleAgentChange = (event) => {
  //   const newTitle = event.target.value;
  //   setEditableTitle(newTitle);
  //   updateTitleInFirestore(newTitle);
  // };

  const handleAgentModalToggle = (toggle) => {
    // Check if an explicit boolean value was passed as the argument
    if (typeof toggle === "boolean") {
      // If true or false was passed, set the state directly
      setIsAgentModalOpen(toggle);
      // If explicitly setting to false (closing), also close the new agent form
      if (!toggle) {
        setIsNewAgentOpen(false);
      }
    } else {
      // If no argument (or a non-boolean) was passed, toggle the current state
      setIsAgentModalOpen((prevState) => {
        const newState = !prevState; // Calculate the new state (opposite of previous)
        // If the toggle results in the modal being closed (newState is false)
        if (!newState) {
          setIsNewAgentOpen(false); // Also close the new agent form
        }
        return newState; // Return the calculated new state
      });
    }
    // The line below was commented out, keep it that way unless you specifically need it
    // setIsDrawerOpen(!isDrawerOpen);
  };

  const handleNewAgentToggle = (toggle) => {
    setIsNewAgentOpen(toggle);
    setIsAgentModalOpen(true);

    if (toggle) {
      setIsAgentModalOpen(true);
    }
  };

  const handleDocModalToggle = (toggle) => {
    // Check if an explicit boolean value was passed as the argument
    if (typeof toggle === "boolean") {
      // If true or false was passed, set the state directly
      setIsDocModalOpen(toggle);
      // If explicitly setting to false (closing), also close the new agent form
      if (!toggle) {
        setIsNewDocOpen(false);
      }
    } else {
      // If no argument (or a non-boolean) was passed, toggle the current state
      setIsDocModalOpen((prevState) => {
        const newState = !prevState; // Calculate the new state (opposite of previous)
        // If the toggle results in the modal being closed (newState is false)
        if (!newState) {
          setIsNewDocOpen(false); // Also close the new agent form
        }
        return newState; // Return the calculated new state
      });
    }
    // The line below was commented out, keep it that way unless you specifically need it
    // setIsDrawerOpen(!isDrawerOpen);
  };

  const handleNewDocToggle = (toggle) => {
    setIsNewDocOpen(toggle);
    setIsDocModalOpen(true);

    if (toggle) {
      setIsDocModalOpen(true);
    }
  };

  const onDrop = useCallback(acceptedFiles => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.error('file reading has failed');
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl === 'string') {
          const base64 = dataUrl.split(',')[1];
          const mimeType = file.type;
          const prompt = `"${file.name}". Create a transcription with time codes and speakers. Include the file name at the top`;
          
          sendMessage(prompt, { file: { base64, mimeType }, ensureOpen: true });
        }
      }
      reader.readAsDataURL(file);
    }
  }, [sendMessage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  useImperativeHandle(ref, () => ({
    processAgent: async () => {
      console.log("GeminiComponent: processAgent triggered");
      const text = await handleAnalyzeClick(true);
      if (text) {
        await handleBuildCanvas(text);
      }
    },
    processDocumentCreator: async () => {
      console.log("GeminiComponent: processDocumentCreator triggered");
      if (currentDoc) {
        const prompt = "Using the data and analysis provided earlier in this chat, create a " + currentDoc.name + " " + currentDoc.desc + " using this as a template: " + currentDoc.content;
        const text = await sendMessage(prompt, { ensureOpen: true });
        if (text && isAgentMode) {
          console.log("GeminiComponent: Document created, building canvas...");
          await handleBuildCanvas(text);
        }
      } else {
        console.log("No current document selected for processDocumentCreator");
      }
    },
    exportToGDocs,
    exportToGSlides,
    // exportToHubspot,
    exportToJira,
    exportToLinear,
    exportToConfluence,
  }));

  // useEffect(() => {
  //   const fetchMergeAccountToken = async () => {
  //     if (currentUser?.uid) {
  //       const userDocRef = doc(db, "users", currentUser.uid);
  //       const docSnap = await getDoc(userDocRef);
  //       if (docSnap.exists() && docSnap.data().mergeAccountToken) {
  //         setMergeAccountToken(docSnap.data().mergeAccountToken);
  //       }
  //     }
  //   };
  //   fetchMergeAccountToken();
  // }, [currentUser]);

  // const { open, isReady } = useMergeLink({
  //   linkToken: mergeLinkToken,
  //   onSuccess: async (publicToken) => {
  //     const result = await exchangePublicToken({ publicToken });
  //     if (result.data.success) {
  //       setMergeAccountToken(result.data.accountToken);
  //     }
  //   },
  // });

  // const handleMergeLink = async (categories) => {
  //   try {
  //     const result = await createMergeLinkToken({ 
  //       organizationName: "DataSculpture", 
  //       categories: categories 
  //     });
  //     if (result.data.success) {
  //       setMergeLinkToken(result.data.linkToken);
  //     }
  //   } catch (error) {
  //     console.error("Error creating merge link token:", error);
  //     setMessages((prev) => [...prev, { text: `Error creating link token: ${error.message}`, sender: "System" }]);
  //   }
  // };

  // useEffect(() => {
  //   if (mergeLinkToken) {
  //     open();
  //   }
  // }, [mergeLinkToken, open]);

  const [agentMode, setAgentMode] = useState(queryParams.get("agentMode") === "true");

  const toggleAgentMode = () => {
    const newAgentMode = !agentMode;
    setAgentMode(newAgentMode);
    const url = new URL(window.location);
    if (newAgentMode) {
      url.searchParams.set("agentMode", "true");
    } else {
      url.searchParams.delete("agentMode");
    }
    window.history.replaceState({}, '', url);
   };  

  return (
    <div ref={chatContainerRef} style={{ height: isResponseMinimized ? "60%" : "0px" }}>

      {isJiraModalOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)", padding: "20px", borderRadius: "5px", color: "black" }}>
            {isLoading ? (
              <div>
                <h2>Creating Jira Issue(s)...</h2>
                <p>Please wait a moment.</p>
              </div>
            ) : (
              <>
                <h1>Export to Jira</h1>
                <p>Select a project to export the data to.</p>
                <select onChange={(e) => handleJiraProjectChange(e.target.value)} value={selectedJiraProject} disabled={isLoading}>
                  <option value="">Select Project</option>
                  {jiraProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <button onClick={handleCreateJiraIssue} disabled={isLoading || !selectedJiraProject}>Create Issue</button>
                <button onClick={() => setIsJiraModalOpen(false)} disabled={isLoading}>Close</button>
              </>
            )}
          </div>
        </div>
      )}

      {isConfluenceModalOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)", padding: "20px", borderRadius: "5px", color: "black" }}>
            <h1>Export to Confluence</h1>
            <select onChange={(e) => setSelectedConfluenceSpace(e.target.value)} value={selectedConfluenceSpace}>
              <option value="">Select Space</option>
              {confluenceSpaces.map(space => (
                <option key={space.id} value={space.id}>{space.name}</option>
              ))}
            </select>
            <button onClick={handleCreateConfluencePage}>Create Page</button>
            <button onClick={() => setIsConfluenceModalOpen(false)}>Close</button>
          </div>
        </div>
      )}

      {isLinearModalOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)", padding: "20px", borderRadius: "5px", color: "black" }}>
            {isLoading ? (
              <div>
                <h2>Creating Linear Issue(s)...</h2>
                <p>Please wait a moment.</p>
              </div>
            ) : (
              <>
                <h1>Export to Linear</h1>
                <p>Select a team to export the data to.</p>
                <select onChange={(e) => setSelectedLinearTeam(e.target.value)} value={selectedLinearTeam} disabled={isLoading}>
                  <option value="">Select Team</option>
                  {linearTeams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
                <button onClick={handleCreateLinearIssue} disabled={isLoading || !selectedLinearTeam}>Create Issue</button>
                <button onClick={() => setIsLinearModalOpen(false)} disabled={isLoading}>Close</button>
              </>
            )}
          </div>
        </div>
      )}

      {isLinearConnectModalOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1001, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)", padding: "20px", borderRadius: "5px", color: "black", textAlign: "center" }}>
            <h2>Connect to Linear</h2>
            <p style={{color: "black"}}>Please connect your Linear account to create issues.</p>
            <button onClick={() => { authorizeLinear(); setIsLinearConnectModalOpen(false); }}>Connect to Linear</button>
            <button onClick={() => setIsLinearConnectModalOpen(false)} style={{ marginLeft: '10px' }}>Cancel</button>
          </div>
        </div>
      )}


      {isAtlassianConnectModalOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1001, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)", padding: "20px", borderRadius: "5px", color: "black", textAlign: "center" }}>
            <h2>Connect to {atlassianService}</h2>
            <p style={{color: "black"}}>Please connect your Atlassian account to create {atlassianService} issues/pages.</p>
            <button onClick={() => { authorizeAtlassian(); setIsAtlassianConnectModalOpen(false); }}>Connect to Atlassian</button>
            <button onClick={() => setIsAtlassianConnectModalOpen(false)} style={{ marginLeft: '10px' }}>Cancel</button>
          </div>
        </div>
      )}

      <form className="form" onSubmit={handleSubmit}>

             {isLoading || isSending ? (
                <div className="loading-indicator">
                  <style>
                    {`
                      @keyframes ai-spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                      .ai-loading-circle {
                        position: absolute;
                        top: 8px; left: 9px;
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        border: 2px solid #e49696ff;
                        border-top: 2px solid #55c6ffff;
                        border-right: 2px solid #92bddaff;
                        box-shadow: 0 0 16px #557affff, 0 0 5px #5560ffff, inset 0 0 12px #92bddaff, inset 0 0 5px #ff7f55ff;
                        animation: ai-spin 1s linear infinite;
                        margin: 0px;
                        flex-shrink: 0;
                      }
                    `}
                  </style>
                  </div>
                ) : null }    
            
               <div className="ai-loading-circle" style={{ position: "absolute" }}></div> 

              <div
                className={`agent-mode-toggle ${agentMode ? "active" : ""}`}
                onClick={() => toggleAgentMode()}
                style={{   width: "40px", height: "40px", paddingRight: "0px", borderRadius: "100%", margin: "4px", cursor: "pointer", flexShrink: 0 }}
              >

               <div
                className="agent-mode-icon"
                dangerouslySetInnerHTML={{ __html: aiIconSvg }}
                style={{ position: "absolute", top: "13px", left: "13px"}}
              />

            </div>

  

        {/* <button
          className="submit"
          disabled={isLoading || isSending} // Disable if loading OR chat not ready OR already sending
        >
          <svg
            width="24"
            height="24"
            viewBox="0 2 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </button>  */}

        

        {/* <button
          type="button"
          onClick={runOpenRouterTest}
          className="submit"
          style={{ marginLeft: "10px", backgroundColor: "#555", width: "auto", padding: "0 10px" }}
          title="Test OpenRouter"
        >
          OR
        </button> */}

        {/* <button className="agent" type="button" onClick={() => toggleResponseView()}>
          {isResponseMinimized ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="-10 0 24 24" width={26} height={20} id="angle-up">
              <path
                fill="var(--Grey7)"
                d="M17,13.41,12.71,9.17a1,1,0,0,0-1.42,0L7.05,13.41a1,1,0,0,0,0,1.42,1,1,0,0,0,1.41,0L12,11.29l3.54,3.54a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29A1,1,0,0,0,17,13.41Z"
              ></path>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="-10 0 24 24" width={26} height={20} id="angle-down">
              <path fill="var(--Grey7)" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"></path>
            </svg>
          )}
        </button> */}

        <div {...getRootProps()} style={{width: "100%", flexGrow: 1}}>
          <input {...getInputProps()} />
          <textarea
            className="textarea"
            type="text"
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onClick={openResponseView}
            placeholder={isDragActive ? "Drop the file to send it for analysis..." : isAgentRunning ? "Agent running..." : "What are you interested in?"}
            // disabled={isLoading || !chat || isSending} // Disable if loading OR chat not ready OR already sending
            style={ isDragActive ? { border: "2px dashed #2196f3" } : {} }
          />
        </div>


          {/* AGENT */}
        <button
            type="button"
            ref={agentButtonRef}
            className={`resFooterButton ${isAgentModalOpen ? "active" : ""}`}
            style={{
              position: "relative",
              left: "-8px",
              display: "flex",
              alignItems: "center",
              textAlign: "left",
              // minWidth: "150px",
              maxWidth: "200px",
              flexDirection: "row",
              justifyContent: "space-between",
              // paddingRight: "10px",
              marginRight: "0px",
              textWrap: "nowrap",

            }}
            onClick={handleAgentModalToggle}
          >
            <div style={{top: "-2px", position: "relative"}}>
              <span style={{ fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>ANALYZE</span>
              <br></br>
              {currentAgent?.name || "Select Agent"}
            </div>

            <div>
              {isAgentModalOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-10 0 24 24" width={26} height={20} id="angle-up">
                  <path
                    fill="var(--Grey7)"
                    d="M17,13.41,12.71,9.17a1,1,0,0,0-1.42,0L7.05,13.41a1,1,0,0,0,0,1.42,1,1,0,0,0,1.41,0L12,11.29l3.54,3.54a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29A1,1,0,0,0,17,13.41Z"
                  ></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-10 0 24 24" width={26} height={20} id="angle-down">
                  <path fill="var(--Grey7)" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"></path>
                </svg>
              )}
            </div>
          </button>

         {/* DOCUMENT */}
          <button
            type="button"
            ref={docButtonRef}
            className={`resFooterButton ${isDocModalOpen ? "active" : ""}`}
            style={{
              position: "relative",
              left: "-8px",
              paddingTop: "-8px",              
              display: "flex",
              alignItems: "center",
              textAlign: "left",
              // minWidth: "150px",
              maxWidth: "200px",
              flexDirection: "row",
              justifyContent: "space-between",
              // paddingRight: "10px",
              marginRight: "0px",
              textWrap: "nowrap",
            }}
            onClick={handleDocModalToggle}
          >
            <div style={{top: "-2px", position: "relative"}}>
              <span style={{ fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>BUILD</span>
              <br></br>
              {currentDoc?.name || "Select document"}
            </div>

            <div>
              {isDocModalOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-10 0 24 24" width={26} height={20} id="angle-up">
                  <path
                    fill="var(--Grey7)"
                    d="M17,13.41,12.71,9.17a1,1,0,0,0-1.42,0L7.05,13.41a1,1,0,0,0,0,1.42,1,1,0,0,0,1.41,0L12,11.29l3.54,3.54a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29A1,1,0,0,0,17,13.41Z"
                  ></path>
                </svg>
                
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-10 0 24 24" width={26} height={20} id="angle-down">
                  <path fill="var(--Grey7)" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"></path>
                </svg>
              )}
            </div>
          </button>


      </form>

      <br />
      
      <div style={{ position: "relative", zIndex: 2000 }}>
        <div ref={agentSelectorRef} style={{  position: "absolute", top: "-38px", right: "6px", paddingRight: "5px", float: "right" }}>
          <AgentSelector // Ensure it takes full width
            closeModal={() => handleAgentModalToggle(false)} // Pass function to explicitly close the modal
            isOpen={isAgentModalOpen} // Pass the state to control visibility
            onClose={() => handleAgentModalToggle(false)} // Pass the close handler
            handleNewAgentToggle={handleNewAgentToggle} // Pass the handler for the "Create New Agent" button
            isNewAgentOpen={isNewAgentOpen} // Pass the state for the new agent form
            setCurrentAgent={handleAgentSelectionFromSelector} // Use the stable handler
            setAvailableAgentsList={setAvailableAgents} // Pass setter for AgentSelector to populate
          />
        </div>

        <div ref={docSelectorRef} style={{ position: "absolute", top: "-38px", right: "6px", paddingRight: "5px", float: "right" }}>
          <DocSelector // Ensure it takes full width
            closeModal={() => handleDocModalToggle(false)} // Pass function to explicitly close the modal
            isOpen={isDocModalOpen} // Pass the state to control visibility
            onClose={() => handleDocModalToggle(false)} // Pass the close handler
            handleNewDocToggle={handleNewDocToggle} // Pass the handler for the "Create New Doc" button
            isNewDocOpen={isNewDocOpen} // Pass the state for the new doc form
            setCurrentDoc={handleDocSelectionFromSelector}
            setAvailableDocsList={setAvailableDocs}
          />
        </div>
      </div>

      <div
        className="response-wrapper"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          position: "relative", // Required for 'top' positioning to work
          opacity: isResponseMinimized ? 0 : 1,
          // maxHeight: isResponseMinimized ? "0px" : (isAgentMode && isSequenceRunningRef ? "115px" : "85vh"),
          maxHeight: isResponseMinimized ? "0px" : "85vh",
          visibility: isResponseMinimized ? "hidden" : "visible",
          transform: isResponseMinimized ? "translateY(-10px)" : "translateY(0)",
          overflow: "hidden",
          transition: "all 0.35s ease-out",
        }}
      >
        {/* <div className="response-header">

        </div> */}

        {/* ///// -- VectorDB -- ///// */}

        {/* <input
            type="file"multiple
            onChange={handleFileChange} 
            />
          <button onClick={handleSearchSubmit}>Search VertexDB</button>
        <VertexDB searchString={newMessage} files={uploadedFiles} uid={auth}/>   */}

        {/* --- Scrollable Message Container --- */}

        <div
          className="response-content" // Add specific styles for this
          id="responseText"
          ref={messagesEndRef} // Add a ref for auto-scrolling (implement if not already done)
          style={{
            flexGrow: 1, // Allow this area to grow and shrink
            position: "relative", // Required for 'top' positioning to work
            overflowY: "auto", // Make it scrollable
            // maxHeight: isResponseMinimized ? "65px" : (isAgentMode ? "115px" : "65vh"),
            maxHeight: isResponseMinimized ? "0px"  : "65vh",
            // minHeight: isAgentMode ? "0px" : "65vh", // Minimum height for the response area
            display: "flex", // Always 'flex'; visibility controlled by opacity/top
            flexDirection: "column",
          }}
        >
          {/* Map over messages and render individually */}
          {messages.map((message, index) => {
            // Determine if markdown parsing is needed (only for AI messages)
            const requiresParsing = message.sender === "AI";
            // Parse ONLY if needed. Use a library like DOMPurify here for extra safety if desired.
            const messageHtml = requiresParsing ? marked(message.text) : null;
            const isUser = message.sender === "User";
            const isAI = message.sender === "AI";
            // const isSystem = message.sender === "System";
            
            return (
                        
              <div>


              <div
                key={index} // Use index as key (consider a more stable ID if messages can change order/be deleted)
                className={`message-bubble ${isUser ? "user-message" : isAI ? "ai-message" : "system-message"}`} // Add classes for styling based on sender
              >
                  <div>
                  {/* Conditionally render icon */}
                  {isUser && <span className="message-icon" dangerouslySetInnerHTML={{ __html: userIconSvg }}></span>}
                  {isAI && <span className="message-icon" dangerouslySetInnerHTML={{ __html: aiIconSvg }}></span>}

                  {/* Render content */}
                  {requiresParsing ? (
                    // Use dangerouslySetInnerHTML ONLY for the parsed AI message content
                    // This significantly reduces the risk compared to rendering the whole history
                    <div className="message-text" dangerouslySetInnerHTML={{ __html: messageHtml }}></div>
                  ) : (
                    // Render User and System messages as plain text
                    <div className="message-text">{message.text}</div>
                  )}
                  </div>
              </div>
              
               <div>
                    {isAI && showAnalyzeButton && !message[0] && (
                      <div className="message-actions">
                        <span style={{ fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>Send to:</span>
                        <button className="resFooterButton" onClick={() => handleBuildCanvas(message.text)}>
                          Smart Build
                        </button>
                        {/* <button className="resFooterButton" onClick={() => handleSendData(message.text)}>
                          Text Editor
                        </button>
                        <button className="resFooterButton" onClick={() => handleSendDataToFM(message.text)}>
                          Data Visual
                        </button> */}

                      <button className="resFooterButton" onClick={() => sendToGDocs(message.text)}>
                        Google Docs
                      </button>
                          
                      <button className="resFooterButton" onClick={() => sendToGSlides(message.text)}>
                        Google Slides
                      </button>

                      </div>
                      
                    )}
                </div>
            </div>
            );
            
          })}


          
          {/* Loading Indicator (styled like a message) */}
          {isLoading || isSending && (
            <div className="message-bubble ai-message">
              {/* <style>
                {`
                  @keyframes ai-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  .ai-loading-circle {
                    position: absolute;
                    top: 8px; left: 9px;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    border: 2px solid #f0acacff;
                    border-top: 2px solid #55c6ffff;
                    border-right: 2px solid #92bddaff;
                    box-shadow: 0 0 16px #557affff, 0 0 5px #5560ffff, inset 0 0 20px #92bddaff, inset 0 0 5px #ff7f55ff;
                    animation: ai-spin 1s linear infinite;
                  }
                `}
              </style> */}
              {/* <div style={{ position: "relative", display: "inline-flex", width: "34px", height: "34px", alignItems: "center", justifyContent: "center" }}>
                <div className="ai-loading-circle"></div>
              </div> */}
              <div className="message-text">
                <span className="message-icon" style={{ position: "relative", zIndex: 1 }} dangerouslySetInnerHTML={{ __html: aiIconSvg }}></span>
                <p>Thinking...</p> 
              </div>
            </div>
          )}
          {/* End of messages div - for auto-scroll ref */}
          {/* <div ref={messagesEndRef} /> */} {/* If using scrollIntoView */}
        </div>
        <div
          className="responseFooter"
          style={{
            display: isDrawerOpen ? "block" : "none",
            transition: "0.35s",
          }}
        >

          {/* {showAnalyzeButton && messages.length > 0 && (
            <button className="resFooterButton" onClick={handlePRDclick}>
              Create Product Requirements
            </button>
          )}

          {showAnalyzeButton && messages.length > 0 && (
            <button className="resFooterButton" onClick={handleMPclick}>
              Create Marketing Plan
            </button>
          )}
          */}
          {/* {showAnalyzeButton && (
           (messages.length>0) && (

          <button className="resFooterButton" onClick={copyToClipboard}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 2 24 20" width={24} height={16} fill="#D9D9D9" id="clipboard">
              <path d="M13,14H9a1,1,0,0,0,0,2h4a1,1,0,0,0,0-2ZM17,4H15.82A3,3,0,0,0,13,2H11A3,3,0,0,0,8.18,4H7A3,3,0,0,0,4,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V7A3,3,0,0,0,17,4ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm8,14a1,1,0,0,1-1,1H7a1,1,0,0,1-1-1V7A1,1,0,0,1,7,6H8V7A1,1,0,0,0,9,8h6a1,1,0,0,0,1-1V6h1a1,1,0,0,1,1,1Zm-3-9H9a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2Z"></path>
            </svg>
          </button>
           )
          )} */}
        </div>
      </div>
    </div>
  );
});

import PropTypes from "prop-types";

GeminiComponent.propTypes = {
  onSendData: PropTypes.func.isRequired,
  showAnalyzeButton: PropTypes.bool,
  onTableDataParsed: PropTypes.func,
  onCreateVisualization: PropTypes.func,
  authUser: PropTypes.object,
  setComponents: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  onSlideCreated: PropTypes.func,
  onRequestGoogleSignIn: PropTypes.func,
  createGoogleDocApiCall: PropTypes.func,
  exportedImages: PropTypes.object,
};

export default GeminiComponent;
